import { Card, Form, message } from '@nackle/paper';
import isEmpty from 'lodash/isEmpty';
import React, { Component, ReactNode } from 'react';
import { CardHeader } from 'shared/card-header';
import { JsonEditor } from 'shared/code-editor';
import { Grid } from 'shared/grid';
import { HasPermission } from 'shared/HasPermission';
import { ValidationMapOf } from 'shared/helper-text-helpers';
import ImageUpload from 'shared/image-upload';
import { Cabinet } from 'shared/interfaces';
import SaveButton from 'shared/save-button';
import { validateCabinet } from './validation';

interface ArcadeCabinetFormProps {
    readonly cabinet: Cabinet;
    readonly setArcadeCabinetValue: any;
    readonly createArcadeCabinet: (cabinet: Cabinet) => unknown;
    readonly loadArcadeCabinetForEditing: (cabinetId: string) => unknown;
    readonly updateArcadeCabinet: (cabinet: Cabinet) => unknown;
    readonly closeAfterSave: any;
}

interface State {
    validationErrors: ValidationMapOf<Cabinet>;
}

export default class ArcadeCabinetForm extends Component<ArcadeCabinetFormProps, State>{
    public state: State = {
        validationErrors: {},
    };

    // Workaround the shared `toaster` being covered up by the drawer by instantiating a toaster AFTER this component renders
    // private localToaster: Toaster;

    componentDidMount(): void {
        const { cabinet: { cabinetId } } = this.props;
        if (cabinetId) {
            this.props.loadArcadeCabinetForEditing(cabinetId);
        }
    }

    clearValidationError = (name: string) => {
        this.setState((state: State) => {
            const validationErrors = { ...state.validationErrors };
            delete validationErrors[name];
            return { validationErrors };
        });
    };

    setValue = (name, value = undefined) => {
        this.props.setArcadeCabinetValue(name, value);
        this.clearValidationError(name);
    };

    handleImage = image => this.setValue('image', image);
    handleBannerImage = image => this.setValue('bannerImage', image);
    removeImage = () => this.setValue('image');
    removeBannerImage = () => this.setValue('bannerImage');

    handleChangeMetaDataValues = (metaDataValues: string) => this.props.setArcadeCabinetValue('metaDataValues', metaDataValues);

    saveArcadeCabinet = async () => {
        try {
            const { cabinet, cabinet: { cabinetId } } = this.props;

            const validationErrors = validateCabinet(cabinet);
            this.setState({ validationErrors });
            if (!isEmpty(validationErrors)) {
                message.warning({
                    content: 'One or more fields are invalid',
                });
                throw new Error('One or more fields are invalid');
            }

            if (cabinetId) {
                await this.props.updateArcadeCabinet(cabinet);
            } else {
                await this.props.createArcadeCabinet(cabinet);
                this.props.closeAfterSave();
            }

        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    render() {
        const {
            cabinet: {
                cabinetId,
                image,
                bannerImage,
                metaDataValues,
                name,
            },
        } = this.props;

        const {
            validationErrors,
        } = this.state;

        let saveButton: ReactNode;
        const cabinetExists = Boolean(cabinetId);
        if (cabinetExists) {
            saveButton = (
                <HasPermission app="arcade-api" permission="edit-cabinet">
                    {(hasPermission: boolean) =>
                        <SaveButton
                            disabled={!hasPermission}
                            id="saveArcadeCabinetButton"
                            save={this.saveArcadeCabinet}
                            successMessage="Cabinet updated successfully"
                        >
                            Save
                        </SaveButton>
                    }
                </HasPermission>
            );
        } else {
            saveButton = (
                <SaveButton
                    id="saveArcadeCabinetButton"
                    save={this.saveArcadeCabinet}
                    successMessage="Cabinet created successfully"
                >
                    Create
                </SaveButton>
            );
        }

        return (
            <Card>
                <CardHeader
                    className="arcade-card-header"
                    right={saveButton}
                    title={name}
                />
                <Grid style={{paddingBottom: 10}}>
                    <Form.Item className="thumbnail-image" label="Image">
                        <ImageUpload
                            imageUrl={image}
                            removeImage={this.removeImage}
                            handleImage={this.handleImage}
                        />
                    </Form.Item>
                    <Form.Item className="banner-image" label="Banner Image">
                        <ImageUpload
                            imageUrl={bannerImage}
                            removeImage={this.removeBannerImage}
                            handleImage={this.handleBannerImage}
                        />
                    </Form.Item>
                </Grid>
                <Form layout="vertical">
                    <Form.Item
                        label="Additional Configuration (JSON format)"
                        {...validationErrors.metaDataValues}
                        style={{ display: 'block' }}
                    >
                        <JsonEditor
                            onChange={this.handleChangeMetaDataValues}
                            value={metaDataValues === null ? '' : metaDataValues}
                        />
                    </Form.Item>
                </Form>
            </Card>
        );
    }
}
